"use client"

import { cn } from "@/lib/utils"
import { BlogSection } from "@/types/blog"

interface TableOfContentsProps {
  sections: BlogSection[]
  activeSection?: string
  className?: string
  onSectionClick?: (sectionId: string) => void
}

export function TableOfContents({
  sections,
  activeSection,
  className,
  onSectionClick
}: TableOfContentsProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()

    // Try to find element by ID
    let element = document.getElementById(id)

    // If not found, try to find heading with matching text
    if (!element) {
      const headings = document.querySelectorAll('h1, h2, h3')
      headings.forEach((heading) => {
        const text = heading.textContent?.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        if (text === id) {
          element = heading as HTMLElement
        }
      })
    }

    if (element) {
      // Update active section immediately on click
      onSectionClick?.(id)

      const offset = 120
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      })
    }
  }

  if (sections.length === 0) {
    return null
  }

  return (
    <nav
      className={cn("", className)}
      aria-label="Table of contents"
    >
      <h2 className="text-[11px] font-medium text-text-muted uppercase tracking-wider mb-3">
        On This Page
      </h2>
      <ul className="space-y-1.5 sm:space-y-2">
        {sections.map((section) => (
          <li
            key={section.id}
            style={{
              paddingLeft: section.level > 1 ? `${(section.level - 1) * 12}px` : undefined
            }}
          >
            <a
              href={`#${section.id}`}
              onClick={(e) => handleClick(e, section.id)}
              className={cn(
                "block text-xs sm:text-sm py-1 transition-colors border-l-2 pl-3 -ml-[2px]",
                activeSection === section.id
                  ? "text-primary border-primary font-medium"
                  : "text-text-muted border-transparent hover:text-text hover:border-border"
              )}
            >
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
